import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL } from '../../data/site';
import { logConversionSkipped, trackEvent, trackLeadConversion } from '../../utils/analytics';
import { MAX_IMAGE_MB, validateImage } from '../../services/imageService';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface Selected {
  file: File;
  preview: string;
  /** Udfyldt hvis filen ikke kan bruges — blokerer indsendelse indtil den fjernes. */
  problem: string | null;
}
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  /** Id på en påbegyndt, ufærdig henvendelse — genbruges ved nyt forsøg. */
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const invalid = selected.filter(item => item.problem);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function removeImage(index: number) {
    setSubmitError('');
    setSelected(current => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-257" data-mp="2x33matdgu56u">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-258" data-mp="2x1is54v2co9i">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-259" data-mp="3br6rw1nljev4">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-260" data-mp="36u7zponhthzk">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-261" data-mp="36vrlfurx4l8q">
              Fortæl om din opgave
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-262" data-mp="3fuweilkpnmdi">
              Beskriv kort hvad du skal have lavet. Du må meget gerne vedhæfte billeder, så er det nemmere at
              vurdere opgaven.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-263" data-mp="yb5qiasqy4z">
              {settings.phone && <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-264" data-mp="5xe3risd043g">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-265" data-mp="3sbns5rwahc48" />
                  {settings.phone}
                </LinkButton>}
              <LinkButton href={`mailto:${CONTACT_EMAIL}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-266" data-mp="3efagx0icm6y7">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-267" data-mp="39duxtdzqeaig" />
                {CONTACT_EMAIL}
              </LinkButton>
            </div>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-268" data-mp="13is7u9gas364">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-269" data-mp="8xo3n8x9l81j">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-270" data-mp="3zctsy6yyxjb" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-271" data-mp="3w2vtwwz332yi">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-272" data-mp="3r9swgdr6r9d1">
                LYKKEVÆRK vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-273" data-mp="3he91puocygpv">
                Send en opgave mere
              </Button>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={async event => {
          event.preventDefault();
          if (submitting) return;
          if (!validate()) return;
          if (invalid.length > 0) {
            setSubmitError(invalid[0].problem ?? '');
            return;
          }
          setSubmitting(true);
          setSubmitError('');
          const result = await createEnquiry({
            ...form,
            files: selected.map(item => item.file),
            draftId
          });
          setSubmitting(false);
          if (!result.ok) {
            // Samme dokument genbruges ved næste forsøg, så der ikke opstår dubletter.
            setDraftId(result.draftId);
            setSubmitError(result.error ?? 'Henvendelsen kunne ikke sendes. Prøv igen, eller ring til os i stedet.');
            logConversionSkipped(result.error ?? 'indsendelsen fejlede');
            return;
          }

          // Herfra er henvendelsen oprettet, alle billeder uploadet, stierne
          // gemt og statussen sat til "Ny henvendelse".
          void trackEvent('generate_lead', {
            lead_source: 'website_contact_form',
            form_name: 'contact_form'
          });
          // Google Ads: én fuldført henvendelse = én konvertering.
          if (result.draftId) trackLeadConversion(result.draftId);
          selected.forEach(item => URL.revokeObjectURL(item.preview));
          setSelected([]);
          setForm(emptyForm);
          setErrors({});
          setDraftId(undefined);
          setSent(true);
        }} data-id="element-274" data-mp="38cjaopye1jsm">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-275" data-mp="1krb35swcb84i">
                Felter markeret med <span className="text-accent" data-id="element-276" data-mp="1ft9jb5r3b4rz">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-277" data-mp="2xqmlzlsnpjkm">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-278" data-mp="32qr7jq8f94hp" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-279" data-mp="3hggjmgi1yswr" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="dig@mail.dk" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-280" data-mp="90dae6bakw6b" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Hvidovre" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-281" data-mp="1qedmccizrmbx" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-282" data-mp="2e1v89rfkrq6z" />

              <div className="mt-5" data-id="element-283" data-mp="3a8gf9g9io341">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-284" data-mp="3eb3bdtd80a71">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-285" data-mp="3jafsrylbop28">
                  <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-286" data-mp="1term4heeb9k" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-287" data-mp="5x4g6ty2osul">Vælg billeder</span>
                  <span className="text-sm text-ink-muted" data-id="element-288" data-mp="avec56rmfxh9">
                    Valgfrit — billeder op til {MAX_IMAGE_MB} MB
                  </span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" disabled={submitting} onChange={event => {
                const input = event.target;
                const picked = Array.from(input.files ?? []).map(file => ({
                  file,
                  preview: URL.createObjectURL(file),
                  problem: validateImage(file)
                }));
                setSelected(current => [...current, ...picked]);
                setSubmitError('');
                input.value = '';
              }} data-id="element-289" data-mp="27pivrmd59l7u" />
                </label>

                {invalid.length > 0 && <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-290" data-mp="x549kjmt8b6">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-291" data-mp="9x1ys2spd1e5" />
                    <span data-id="element-292" data-mp="1ufccvgzcc18z">
                      {invalid.map(item => <span key={item.file.name} className="block" data-id="element-293" data-mp="1ud9i6jn8tjxx">
                          {item.problem}
                        </span>)}
                    </span>
                  </p>}

                {selected.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-294" data-mp="1ghnuqgwnr87m">
                    {selected.map((item, index) => <li key={`${item.file.name}-${index}`} className={`overflow-hidden rounded-xl border ${item.problem ? 'border-danger' : 'border-line'}`} data-id="element-295" data-mp="294iflexe09lm" data-mpi={index}>
                        <div className="relative" data-id="element-296" data-mp="1zcuq3jke52ms">
                          <img src={item.preview} alt={item.file.name} className="aspect-square w-full object-cover" data-id="element-297" data-mp="2wv4ikrsjb3or" />
                          <button type="button" onClick={() => removeImage(index)} aria-label={`Fjern billede ${item.file.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-298" data-mp="q6m1bir28ymz">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-299" data-mp="bh6e2cqqnbc1" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-300" data-mp="3todnuhmfgqds">
                          <span className={`truncate text-xs ${item.problem ? 'text-danger' : 'text-ink-muted'}`} data-id="element-301" data-mp="2groh16qra2q">
                            {item.file.name}
                          </span>
                          <button type="button" onClick={() => removeImage(index)} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-302" data-mp="26xxxub6ofyde">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-303" data-mp="2kojctot0fo60">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-304" data-mp="2unilh7drzjt0" />
                  Tjek de markerede felter, før du sender.
                </p>}

              {submitError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-305" data-mp="10b2f86kzc5o5">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-306" data-mp="16oyaqa0gazsr" />
                  {submitError}
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting || invalid.length > 0} data-id="element-307" data-mp="1fqpf9bj990l1">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-308" data-mp="n2jtclggt5jm" />
                    {selected.length > 0 ? 'Sender og uploader billeder …' : 'Sender …'}
                  </> : 'Send forespørgsel'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}